import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ChevronDown, BookOpen, Lock, Crown, Plus, Minus, GraduationCap } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useAdmin } from '@/hooks/useAdmin';
import { getEntitlements } from '@/utils/entitlements';
import { PlanBadge } from '@/components/subscription/PlanBadge';
import { getDashboardPath } from '@/utils/getDashboardPath';
import CourseDetailModal, { ELITE_COURSE_DETAILS, CourseDetail } from '@/components/elite/CourseDetailModal';

interface MasterClass {
  id: string;
  name: string;
  description: string | null;
  target_age: number | null;
  category_id: string;
  category_name: string;
  subject_group: string;
  is_enrolled: boolean;
  curriculum_track: string | null;
  lesson_count?: number | null;
  module_count?: number | null;
  module_plan?: any[] | null;
}

const isElite = (c: { subject_group?: string; curriculum_track?: string | null }) =>
  c.subject_group === 'Elite Academy' ||
  ['foundation', 'intermediate', 'advanced'].includes(c.curriculum_track || '');

const isFaith = (c: { curriculum_track?: string | null }) => c.curriculum_track === 'faith';

const GROUP_ICONS: Record<string, string> = {
  'Mathematics': '📐',
  'Sciences': '🔬',
  'English & Writing': '📝',
  'Languages': '🌐',
  'Humanities': '📜',
  'Social Sciences': '👥',
  'Technology & Computing': '💻',
  'Arts & Performance': '🎨',
  'Business & Finance': '💼',
  'Life Skills & Wellbeing': '🧘',
  'Christian Studies': '✝️',
  'Bible Journey': '📖',   // faith stream — seeker to scholar progression
  'Faith Stream': '✝️',   // fallback if group name ever differs
  'Careers & Study Skills': '🧭',
  'Astronomy & Space': '🔭',
  'Elite Academy': '👑',
  'General': '📚',
};

const GROUP_ORDER = [
  'Mathematics', 'Sciences', 'English & Writing', 'Languages', 'Humanities',
  'Social Sciences', 'Technology & Computing', 'Arts & Performance',
  'Business & Finance', 'Life Skills & Wellbeing', 'Christian Studies',
  'Bible Journey', // sits right after Christian Studies — Bible-adjacent but distinct
  'Careers & Study Skills', 'Astronomy & Space', 'Elite Academy', 'General',
];

const AGE_RANGES = [
  { key: 'all', label: 'All Ages', min: 0, max: 99 },
  { key: '5-7', label: '5–7', min: 5, max: 7 },
  { key: '8-11', label: '8–11', min: 8, max: 11 },
  { key: '12-14', label: '12–14', min: 12, max: 14 },
  { key: '15-18', label: '15–18', min: 15, max: 18 },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.04 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] } },
};

export default function ChildSubjectsPage() {
  const { childId } = useParams<{ childId: string }>();
  const { user, userProfile, isLoading: authLoading } = useAuth();
  const { isAdmin } = useAdmin();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [classes, setClasses] = useState<MasterClass[]>([]);
  const [childName, setChildName] = useState('Child');
  const [childAge, setChildAge] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [ageRange, setAgeRange] = useState('all');
  const [filterTrack, setFilterTrack] = useState<'all' | 'standard' | 'elite' | 'faith'>('all');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [previewClass, setPreviewClass] = useState<MasterClass | null>(null);
  const [courseDetail, setCourseDetail] = useState<CourseDetail | null>(null);

  const { elite, standard, isPaid } = getEntitlements(userProfile);

  useEffect(() => {
    if (!authLoading && !user) { navigate('/signin', { replace: true }); return; }
    if (childId) loadData();
  }, [childId, userProfile?.facility_id, authLoading]);

  async function loadData() {
    if (!childId) return;
    setLoading(true);
    try {
      const isDemo = childId === '00000000-0000-4000-a000-000000000a4a' || childId === 'demo-ava';

      if (!isDemo && !isAdmin && userProfile?.facility_role !== 'admin') { navigate('/homeschool/parent'); return; }

      // Facility guard: non-homeschool admins have their own portals
      if (!isAdmin && userProfile?.facility_type && !['homeschool', 'family_edu', 'family'].includes(userProfile.facility_type)) {
        if (userProfile.facility_type === 'church') { navigate('/church/admin', { replace: true }); return; }
        if (userProfile.facility_type === 'facility') { navigate('/facility-admin', { replace: true }); return; }
        navigate(getDashboardPath(userProfile), { replace: true });
        return;
      }

      let enrolledIds = new Set<string>();

      if (isDemo) {
        setChildName('Ava (Sample Student)');
        setChildAge(12);

        const { data: show } = await supabase
          .from('demo_child_showcase').select('enrollments').maybeSingle();
        const demoEnrols = (show?.enrollments && Array.isArray(show.enrollments) ? show.enrollments : []) as any[];
        enrolledIds = new Set(demoEnrols.map(e => e.class_id));
      } else {
        if (!userProfile?.facility_id) return;
        const { data: childUser } = await supabase
          .from('users')
          .select('name, age')
          .eq('id', childId)
          .eq('facility_id', userProfile.facility_id)
          .maybeSingle();

        if (childUser) {
          setChildName(childUser.name || 'Child');
          setChildAge(childUser.age && childUser.age > 0 ? childUser.age : null);
        }

        const { data: enrolled } = await supabase
          .from('class_enrollments')
          .select('class_id')
          .eq('student_id', childId!);

        enrolledIds = new Set((enrolled || []).map((e) => e.class_id));
      }

      const { data: allClasses } = await supabase
        .from('classes')
        .select('id, name, description, target_age, category_id, curriculum_track, subject_group, lesson_count, module_count, categories(name)')
        .eq('is_active', true)
        // Both hubs: Master (standard + elite) + Faith (the 9-course Bible Journey stream).
        // The null-curriculum_track filter below already excludes the 45 university classes
        // on the Faith hub.
        .in('facility_id', ['00000000-0000-0000-0000-000000000001', '00000000-0000-0000-0000-000000000002'])
        .order('name');

      const mapped: MasterClass[] = (allClasses || [])
        .filter(c => {
          // Hide Pastoral Ministry and exclude null-track classes
          const n = c.name?.toLowerCase() || '';
          if (n.includes('pastoral ministry')) return false;
          if (!c.curriculum_track) return false;
          return true;
        })
        .map((c) => {
          const catName = (c.categories as any)?.name || 'General';
          const isFaithTrack = c.curriculum_track === 'faith';
          // Bible Journey is now the canonical DB value for these rows, but keep
          // the mapping as belt-and-braces against future data drift.
          const displayGroup = isFaithTrack
            ? 'Bible Journey'
            : ((c as any).subject_group || 'General');
          return {
            id: c.id,
            name: c.name,
            description: c.description,
            target_age: c.target_age,
            category_id: c.category_id,
            category_name: catName,
            subject_group: displayGroup,
            is_enrolled: enrolledIds.has(c.id),
            curriculum_track: (c as any).curriculum_track || null,
            lesson_count: (c as any).lesson_count ? Number((c as any).lesson_count) : null,
            module_count: (c as any).module_count ? Number((c as any).module_count) : null,
            module_plan: null,
          };
        });

      setClasses(mapped);

      // Default: expand groups with ≤ 6 groups visible
      const groups = new Set(mapped.map(c => c.subject_group));
      if (groups.size <= 6) {
        setExpandedGroups(groups);
      } else {
        // Expand first 3
        const sorted = GROUP_ORDER.filter(g => groups.has(g));
        setExpandedGroups(new Set(sorted.slice(0, 3)));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const [showSubscribeModal, setShowSubscribeModal] = useState(false);

  async function enrollChild(classId: string) {
    // Subscription gate: verify standard/elite/faith access based on class
    const targetCls = classes.find(c => c.id === classId);
    const isDemo = childId === '00000000-0000-4000-a000-000000000a4a' || childId === 'demo-ava';
    if (isDemo) {
      alert("This is a preview of the curriculum for the sample student. Enrol your child to customize and assign subjects.");
      return;
    }

    const requiresElite = targetCls ? isElite(targetCls) : false;
    const isFaithCls = targetCls?.curriculum_track === 'faith';
    const hasAccess = isAdmin || (isFaithCls ? isPaid : (requiresElite ? elite : standard));

    if (!hasAccess) {
      setShowSubscribeModal(true);
      return;
    }

    setEnrolling(classId);
    try {
      const targetCls = classes.find(c => c.id === classId);
      await supabase.from('class_enrollments').upsert({
        class_id: classId, student_id: childId!,
        progress_state: { current_unit: 1, current_lesson: 1, total_lessons_completed: 0 },
        mastery_score: 0,
      }, { onConflict: 'class_id,student_id' });
      // Mirror to student_subject_progress so /learn session page can load it
      if (targetCls) {
        await supabase.from('student_subject_progress').upsert({
          user_id: childId!,
          category_id: targetCls.category_id,
          class_id: classId,
          mastery_score: 0,
          current_unit: 1,
          current_lesson: 1,
        }, { onConflict: 'user_id,category_id,class_id' });
      }
      setClasses(prev => prev.map(c => c.id === classId ? { ...c, is_enrolled: true } : c));
    } catch (err) { console.error(err); }
    finally { setEnrolling(null); }
  }

  async function unenrollChild(classId: string) {
    const isDemo = childId === '00000000-0000-4000-a000-000000000a4a' || childId === 'demo-ava';
    if (isDemo) {
      alert("This is a preview of the curriculum for the sample student. Enrol your child to customize and assign subjects.");
      return;
    }

    setEnrolling(classId);
    try {
      await supabase.from('class_enrollments').delete().eq('class_id', classId).eq('student_id', childId!);
      await supabase.from('student_subject_progress').delete().eq('class_id', classId).eq('user_id', childId!);
      setClasses(prev => prev.map(c => c.id === classId ? { ...c, is_enrolled: false } : c));
    } catch (err) { console.error(err); }
    finally { setEnrolling(null); }
  }

  const toggleGroup = (group: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(group) ? next.delete(group) : next.add(group);
      return next;
    });
  };

// Module-scoped plan cache — survives modal opens, resets with session/page reload
const planCache = new Map<string, any[]>();

  /** Build a CourseDetail for the CourseDetailModal from a MasterClass */
  function buildCourseDetail(cls: MasterClass, plan?: any[] | null): CourseDetail {
    const track = cls.curriculum_track || 'foundation';

    // Tier colour + badge driven by curriculum_track or subject_group
    const tierColor =
      track === 'faith' ? '#8A6D3B'
      : track === 'advanced' ? '#C9A84C'
      : track === 'intermediate' ? '#311cefff'
      : cls.subject_group === 'Elite Academy' ? '#D4A843'
      : '#4A90D9';

    const badge =
      track === 'faith' ? 'Bible Journey'
      : track === 'advanced' ? 'Advanced'
      : track === 'intermediate' ? 'Intermediate'
      : cls.subject_group === 'Elite Academy' ? 'Elite Academy'
      : 'Foundation';

    const emoji = GROUP_ICONS[cls.subject_group] || '📚';

    // Prefer DB plan; seed with Elite constant as first paint if DB plan not yet fetched
    let modules = ELITE_COURSE_DETAILS[cls.name] || [];
    const mp = plan || cls.module_plan;
    if (mp && mp.length > 0) {
      modules = (mp as any[]).map((m: any, mi: number) => ({
        module_id: m.module_id ?? mi + 1,
        title: m.title || m.name || `Module ${mi + 1}`,
        lessons: Array.isArray(m.lessons)
          ? m.lessons.map((l: any, li: number) => ({
              lesson_id: l.lesson_id ?? li + 1,
              title: typeof l === 'string' ? l : (l.title || l.name || `Lesson ${li + 1}`),
            }))
          : [],
      }));
    }

    return {
      name: cls.name,
      tierColor,
      badge,
      emoji,
      modules,
    };
  }

  const openDetail = async (cls: MasterClass) => {
    setPreviewClass(cls);
    const cached = planCache.get(cls.id);
    // Instant paint: cache if available, else elite seed or loading shimmer
    setCourseDetail(buildCourseDetail(cls, cached ?? null));

    if (cached) return; // Truth already verified from DB this session

    const { data } = await supabase
      .from('classes')
      .select('module_plan')
      .eq('id', cls.id)
      .maybeSingle();

    const plan = Array.isArray(data?.module_plan) ? data.module_plan : [];
    if (plan.length > 0) {
      planCache.set(cls.id, plan);
      setCourseDetail(buildCourseDetail(cls, plan));
    }
  };

  const filtered = useMemo(() => {
    const range = AGE_RANGES.find(r => r.key === ageRange) || AGE_RANGES[0];
    return classes.filter(c => {
      const q = search.toLowerCase();
      const matchSearch = !q || c.name.toLowerCase().includes(q) || c.category_name.toLowerCase().includes(q) || c.subject_group.toLowerCase().includes(q);
      const matchAge = ageRange === 'all' || !c.target_age ||
        c.curriculum_track === 'faith' ||            // Bible Journey: never age-filtered
        (c.target_age >= range.min && c.target_age <= range.max);
      const matchTrack =
        filterTrack === 'all' ? true
        : filterTrack === 'elite' ? isElite(c)
        : filterTrack === 'faith' ? isFaith(c)
        : !isElite(c) && !isFaith(c);   // 'standard' excludes faith too — its own school
      return matchSearch && matchAge && matchTrack;
    });
  }, [classes, search, ageRange, filterTrack]);

  const enrolledFiltered = useMemo(() => {
    return [...filtered.filter(c => c.is_enrolled)].sort((a, b) => {
      if (a.subject_group === 'Bible Journey' && b.subject_group === 'Bible Journey') {
        return a.id.localeCompare(b.id) || a.name.localeCompare(b.name);
      }
      if (a.subject_group === 'Bible Journey') return -1;
      if (b.subject_group === 'Bible Journey') return 1;
      return a.name.localeCompare(b.name);
    });
  }, [filtered]);
  const availableFiltered = useMemo(() => filtered.filter(c => !c.is_enrolled), [filtered]);

  const groupedAvailable = useMemo(() => {
    const map = new Map<string, MasterClass[]>();
    for (const c of availableFiltered) {
      const arr = map.get(c.subject_group) || [];
      arr.push(c);
      map.set(c.subject_group, arr);
    }
    // Bible Journey IDs encode the canonical sequence (course-1 … course-9).
    // Sort by id for faith, alphabetically by name for all other groups.
    const sortItems = (g: string, items: MasterClass[]) =>
      g === 'Bible Journey'
        ? [...items].sort((a, b) => a.id.localeCompare(b.id))
        : [...items].sort((a, b) => a.name.localeCompare(b.name));
    return GROUP_ORDER
      .filter(g => map.has(g))
      .map(g => ({ group: g, items: sortItems(g, map.get(g)!) }))
      .concat(
        Array.from(map.entries())
          .filter(([g]) => !GROUP_ORDER.includes(g))
          .map(([group, items]) => ({ group, items: sortItems(group, items) }))
      );
  }, [availableFiltered]);

  const enrolledCount = classes.filter(c => c.is_enrolled).length;

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background flex-col gap-4">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 rounded-full border-[3px] border-muted border-t-primary" />
        <p className="text-muted-foreground text-sm">{t('homeschool.subjects.loading')}</p>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-background min-h-screen">
      {/* STICKY TOP BAR */}
      <div className="sticky top-0 z-50 bg-card/95 backdrop-blur-md border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(`/homeschool/child/${childId}`)}
            className="text-muted-foreground hover:text-foreground text-sm transition-colors"
          >
            ← {t('homeschool.subjects.back')}
          </button>
          <span className="text-border">|</span>
          <h1 className="font-bold text-sm sm:text-base text-foreground truncate flex-1 text-white">
            {t('homeschool.subjects.title', { name: childName })}
          </h1>
          <PlanBadge userProfile={userProfile} dark={false} />
          <div className="bg-primary/10 text-primary border border-primary/20 rounded-full px-3 py-1.5 text-xs font-bold whitespace-nowrap">
            <BookOpen className="w-3.5 h-3.5 inline mr-1" />
            {t('homeschool.subjects.enrolledCount', { count: enrolledCount })}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-5">
        {/* HEADER */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-5">
          <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-foreground mb-1 text-white">
            {t('homeschool.subjects.headerTitle', { name: childName })}
          </h2>
          <p className="text-muted-foreground text-sm">
            {childAge && childAge > 0
              ? t('homeschool.subjects.headerSubtitle', { age: childAge, name: childName })
              : `Tap a subject to preview the curriculum · AI adapts to ${childName}'s level`}
          </p>
        </motion.div>

        {/* SEARCH & FILTERS */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="bg-card rounded-2xl border border-border p-3 sm:p-4 mb-5 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={t('homeschool.subjects.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-input rounded-xl text-sm bg-background outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {/* Age dropdown */}
            <select
              value={ageRange}
              onChange={e => setAgeRange(e.target.value)}
              className="px-3 py-2 rounded-xl border border-input bg-background text-sm font-medium text-foreground cursor-pointer focus:border-primary outline-none"
            >
              {AGE_RANGES.map(r => (
                <option key={r.key} value={r.key}>{t(`homeschool.subjects.ageRanges.${r.key}` as any, r.label)}</option>
              ))}
            </select>

            {/* Track pills — Standard / Elite / Bible Journey stand as three equal schools */}
            <div className="flex bg-muted rounded-xl p-1 gap-0.5">
              {[
                { key: 'all' as const, label: t('homeschool.subjects.tracks.all') },
                { key: 'standard' as const, label: t('homeschool.subjects.tracks.standard'), icon: '📘' },
                { key: 'elite' as const, label: t('homeschool.subjects.tracks.elite'), icon: '👑' },
                { key: 'faith' as const, label: 'Bible Journey', icon: '📖' },
              ].map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setFilterTrack(opt.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${filterTrack === opt.key
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                    }`}
                >
                  {opt.icon && <span className="mr-1">{opt.icon}</span>}{opt.label}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* ENROLLED SECTION */}
        {enrolledFiltered.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="mb-8">
            <h3 className="text-xs font-bold text-primary tracking-wider mb-3 uppercase flex items-center gap-2 text-white">
              <BookOpen className="w-3.5 h-3.5" />
              {t('homeschool.subjects.currentlyEnrolled', { count: enrolledFiltered.length })}
            </h3>
            <motion.div variants={containerVariants} initial="hidden" animate="visible"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {enrolledFiltered.map((cls) => (
                <motion.div key={cls.id} variants={itemVariants}
                  className="flex items-center gap-3 p-3 rounded-xl border border-primary/25 bg-primary/5 cursor-pointer hover:bg-primary/10 transition-colors group"
                  onClick={() => openDetail(cls)}
                >
                  <span className="text-lg flex-shrink-0">{GROUP_ICONS[cls.subject_group] || '📚'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm leading-tight truncate text-foreground">{cls.name}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {cls.curriculum_track === 'faith'
                        ? 'All ages · mature content'
                        : cls.target_age ? `Age ${cls.target_age}+` : 'All ages'}
                      {isElite(cls) && (
                        <span className="ml-1.5 text-[9px] font-bold bg-amber-100 text-amber-700 px-1.5 rounded">{t('homeschool.subjects.eliteBadge', '👑 Elite')}</span>
                      )}
                      {isFaith(cls) && (
                        <span className="ml-1.5 text-[9px] font-bold bg-amber-50 text-amber-800 px-1.5 rounded">📖 Bible Journey</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); unenrollChild(cls.id); }}
                    disabled={enrolling === cls.id}
                    className="flex-shrink-0 p-1.5 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors disabled:opacity-50"
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        )}

        {/* AVAILABLE — GROUPED BY DEPARTMENT */}
        <h3 className="text-xs font-bold text-muted-foreground tracking-wider mb-4 uppercase flex items-center gap-2 text-white">
          <GraduationCap className="w-3.5 h-3.5" />
          {t('homeschool.subjects.availableToEnrol', { count: availableFiltered.length })}
        </h3>

        {groupedAvailable.length === 0 ? (
          <div className="bg-card rounded-2xl border border-border p-14 text-center text-muted-foreground">
            {search ? t('homeschool.subjects.noResults', { search }) : t('homeschool.subjects.allEnrolled')}
          </div>
        ) : (
          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-2.5">
            {groupedAvailable.map(({ group, items }) => {
              const isExpanded = expandedGroups.has(group);
              const isEliteGroup = group === 'Elite Academy';
              const isBibleJourneyGroup = group === 'Bible Journey';

              return (
                <motion.div key={group} variants={itemVariants}
                  className="bg-card rounded-2xl border border-border overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  {/* Group header */}
                  <button
                    onClick={() => toggleGroup(group)}
                    className="w-full px-4 py-3.5 flex items-center gap-3 hover:bg-muted/40 transition-colors text-left"
                  >
                    <span className="text-xl flex-shrink-0">{GROUP_ICONS[group] || '📚'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm text-foreground">{t(`homeschool.groups.${group}`, group)}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {t('homeschool.subjects.subjectsAvailable', { count: items.length })}
                      </div>
                    </div>
                    {isEliteGroup && (
                      <span className="flex items-center gap-1 text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                        <Crown className="w-3 h-3" /> ELITE
                      </span>
                    )}
                    {isBibleJourneyGroup && (
                      <span className="flex items-center gap-1 text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full">
                        📖 BONUS: INCLUDED WITH ANY PLAN
                      </span>
                    )}
                    <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] }}
                        className="overflow-hidden"
                      >
                        <div className="px-3 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {items.map((cls) => {
                            const isFaithCls = cls.curriculum_track === 'faith';
                            const locked = isFaithCls ? !isPaid : (isElite(cls) ? !elite : !standard);
                            return (
                              <motion.div
                                key={cls.id}
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.98 }}
                                className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${locked
                                  ? 'bg-muted/30 border-dashed border-border opacity-75'
                                  : 'bg-background border-border hover:border-primary/30 hover:shadow-sm'
                                  }`}
                                onClick={() => openDetail(cls)}
                              >
                                <span className={`text-lg flex-shrink-0 ${locked ? 'grayscale' : ''}`}>
                                  {locked ? <Lock className="w-5 h-5 text-muted-foreground" /> : (GROUP_ICONS[cls.subject_group] || '📚')}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <div className={`font-semibold text-sm leading-tight truncate ${locked ? 'text-muted-foreground' : 'text-foreground'}`}>
                                    {cls.name}
                                  </div>
                                  <div className="text-[11px] text-muted-foreground mt-0.5">
                                    {cls.curriculum_track === 'faith'
                                      ? 'All ages · mature content'
                                      : cls.target_age ? `Age ${cls.target_age}+` : 'All ages'}
                                    {isElite(cls) && (
                                      <span className="ml-1.5 text-[9px] font-bold bg-amber-100 text-amber-700 px-1.5 rounded">{t('homeschool.subjects.eliteBadge', '👑 Elite')}</span>
                                    )}
                                    {isFaith(cls) && (
                                      <span className="ml-1.5 text-[9px] font-bold bg-amber-50 text-amber-800 px-1.5 rounded">📖 Bible Journey</span>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    locked ? navigate('/homeschool/subscription') : enrollChild(cls.id);
                                  }}
                                  disabled={enrolling === cls.id}
                                  className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition-all disabled:opacity-50 ${locked
                                    ? 'bg-amber-500 text-white hover:bg-amber-600'
                                    : 'bg-primary text-primary-foreground hover:opacity-90'
                                    }`}
                                >
                                  {enrolling === cls.id ? '…' : locked ? `👑 ${t('homeschool.subjects.upgrade', 'Upgrade')}` : t('homeschool.subjects.add', '+ Add')}
                                </button>
                              </motion.div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>

      {/* SUBSCRIBE MODAL */}
      <AnimatePresence>
        {showSubscribeModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowSubscribeModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-card rounded-2xl max-w-sm w-full p-8 border border-border shadow-2xl text-center space-y-5"
            >
              <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-extrabold text-foreground text-white">
                {userProfile?.facility_role === 'member'
                  ? 'Ask Your Parents to Unlock'
                  : t('homeschool.subjects.subscribeModal.title')}
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {userProfile?.facility_role === 'member'
                  ? 'Ask your parents to unlock this course or Elite Academy for your family.'
                  : t('homeschool.subjects.subscribeModal.desc', { name: childName })}
              </p>
              <div className="flex flex-col gap-2 pt-2">
                <button
                  onClick={() => navigate('/homeschool/subscription')}
                  className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:opacity-90 transition-opacity"
                >
                  {userProfile?.facility_role === 'member' ? 'View Plans' : t('homeschool.subjects.subscribeModal.viewPlans', 'Upgrade Now')}
                </button>
                <button
                  onClick={() => setShowSubscribeModal(false)}
                  className="w-full py-2.5 rounded-xl text-muted-foreground text-sm hover:text-foreground transition-colors"
                >
                  {t('homeschool.subjects.maybeLater')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* COURSE PREVIEW MODAL — uses CourseDetailModal for rich curriculum teaser */}
      <CourseDetailModal
        course={courseDetail}
        onClose={() => { setPreviewClass(null); setCourseDetail(null); }}
        onEnrol={previewClass ? (
          previewClass.is_enrolled
            ? () => { unenrollChild(previewClass.id); setPreviewClass(null); setCourseDetail(null); }
            : (isElite(previewClass) ? !elite : !standard)
              ? () => navigate('/homeschool/subscription')
              : () => { enrollChild(previewClass.id); setPreviewClass(null); setCourseDetail(null); }
        ) : undefined}
      />
    </motion.div>
  );
}
